import { useEffect, useRef, useState } from 'react';
import InkStroke from '../../../components/base/InkStroke';

const principles = [
  {
    title: 'Plain on the surface, powerful in effect',
    body: 'We do not rely on noise or theatrics. We may look simple; we focus on practical changes that create deep, lasting impact in the way you communicate and operate.',
  },
  {
    title: 'Made to mix with your world',
    body: 'We plug into your teams, culture, and workflows — pairing practical expertise with a careful understanding of how your organisation works, so progress feels collaborative rather than disruptive.',
  },
  {
    title: 'Value that compounds over time',
    body: 'What begins as a modest engagement becomes a strong, growing partnership. Together we create new ideas, stronger outcomes, and sustainable progress that builds year after year.',
  },
];

const story = {
  heading: 'Built on the belief that business is a human endeavour',
  body1:
    'nattō studio was founded with a simple conviction: the most sustainable business results come from relationships built on trust, not transactions built on pressure.',
  body2:
    'We have worked with founders, leadership teams, and organisations across sectors — from emerging ventures to established institutions — always with the same approach: listen first, advise second, and stay long enough to see the impact.',
};

export default function PhilosophySection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="philosophy" ref={sectionRef} className="py-20 md:py-28 px-6 md:px-10 lg:px-16">
      <div className="max-w-5xl mx-auto">
        {/* Section header */}
        <div
          className={`text-center mb-14 md:mb-20 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <p className="font-body text-xs uppercase tracking-wide-custom text-muted-umber mb-3">
            Our Philosophy
          </p>
          <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-medium text-deep-ink tracking-tight-custom">
            Three principles govern all we do
          </h2>
          <InkStroke className="mt-6" />
        </div>

        {/* Principles grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {principles.map((p, i) => (
            <div
              key={p.title}
              className={`transition-all duration-700 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
              style={{ transitionDelay: `${200 + i * 150}ms` }}
            >
              <div className="h-full bg-white/60 rounded-lg p-5 md:p-6 border border-amber/10">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-amber/10 mb-5">
                  <span className="font-display text-sm font-bold text-amber">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                </div>
                <h3 className="font-body text-sm font-semibold text-saddlebrown mb-3 leading-snug">
                  {p.title}
                </h3>
                <p className="font-body text-sm text-muted-umber leading-relaxed">
                  {p.body}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Story block */}
        <div
          className={`mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
          style={{ transitionDelay: '700ms' }}
        >
          <div>
            <p className="font-body text-xs uppercase tracking-wide-custom text-muted-umber mb-3">
              Our Story
            </p>
            <h3 className="font-display text-xl md:text-2xl font-medium text-deep-ink tracking-tight-custom mb-6">
              {story.heading}
            </h3>
            <p className="font-body text-sm text-muted-umber leading-relaxed mb-4">
              {story.body1}
            </p>
            <p className="font-body text-sm text-muted-umber leading-relaxed">
              {story.body2}
            </p>
          </div>
          <div>
            <p className="font-body text-xs uppercase tracking-wide-custom text-muted-umber mb-3">
              Our Approach
            </p>
            <h3 className="font-display text-xl md:text-2xl font-medium text-deep-ink tracking-tight-custom mb-6">
              Understated by design, impactful by practice
            </h3>
            <p className="font-body text-sm text-muted-umber leading-relaxed mb-4">
              We do not chase trends or deliver templated solutions. Every engagement
              begins with a genuine understanding of what makes your organisation unique —
              its culture, its constraints, its ambitions.
            </p>
            <p className="font-body text-sm text-muted-umber leading-relaxed">
              Our recommendations are practical, our methods are transparent, and our
              partnerships are measured in years, not quarters.
            </p>
          </div>
        </div>

        {/* Quote */}
        <div
          className={`mt-16 md:mt-20 text-center transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
          style={{ transitionDelay: '900ms' }}
        >
          <div className="bg-white/60 rounded-lg p-8 md:p-10 border border-amber/10">
            <blockquote className="max-w-2xl mx-auto">
              <p className="font-display text-lg md:text-xl font-medium italic text-saddlebrown leading-relaxed">
                &ldquo;We may appear understated at first glance. We are a dependable,
                long-term partner — consistent, valuable, and committed to helping
                businesses grow.&rdquo;
              </p>
            </blockquote>
          </div>
        </div>
      </div>
    </section>
  );
}